import React, { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  MousePointerClick, Target, FileSpreadsheet,
  Megaphone,
  Store,
  CreditCard,
  PackageOpen,
  Receipt,
  ArrowLeftRight,
  Flag,
  Menu,
  Bell,
  HeadphonesIcon,
  Users,
  ClipboardList,
  Settings,
  Activity,
  ChevronRight,
  LogOut,
  X,
  Briefcase,
  Wallet,
  Banknote,
  UserCog,
  KeyRound,
  Bot, MapPin } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import RoleBadge from '../shared/RoleBadge'
import BrandLogo from '../shared/BrandLogo'
import { getInitials } from '../../lib/utils'
import { usePermission, permissionForPath } from '../../hooks/usePermission'
import { tx } from '../../lib/i18n'

interface NavItem {
  label: string
  href: string
  icon: React.ElementType
}

interface NavGroup {
  label: string
  items: NavItem[]
}

const navGroups: NavGroup[] = [
  {
    label: tx('Overview'),
    items: [
      { label: tx('Dashboard'), href: '/dashboard', icon: LayoutDashboard },
      { label: tx('Customer Analytics'), href: '/customer-analytics', icon: Users },
      { label: tx('Menu & Button Usage'), href: '/usage-analytics', icon: MousePointerClick },
      { label: tx('Business Stats & Targets'), href: '/business-insights', icon: Target },
    ],
  },
  {
    label: tx('Operations'),
    items: [
      { label: tx('Shops'), href: '/shops', icon: Store },
      { label: tx('Shops map'), href: '/shops/map', icon: MapPin },
      { label: tx('Subscriptions'), href: '/subscriptions', icon: CreditCard },
      { label: tx('Plans'), href: '/plans', icon: PackageOpen },
      { label: tx('Plan Promotions'), href: '/subscription-promotions', icon: Flag },
      { label: tx('Sales & Affiliates'), href: '/referrers', icon: Briefcase },
      { label: tx('Consent Documents'), href: '/consents', icon: ClipboardList },
      { label: tx('Announcements'), href: '/announcements', icon: Megaphone },
    ],
  },
  {
    label: tx('Finance'),
    items: [
      { label: tx('Shop Payments'), href: '/shop-payments', icon: Banknote },
      { label: tx('Billing'), href: '/billing', icon: Receipt },
      { label: tx('Money Logs'), href: '/money-logs', icon: ArrowLeftRight },
      { label: tx('Accounting Reports'), href: '/finance-reports', icon: FileSpreadsheet },
      { label: tx('AI Usage'), href: '/ai-usage', icon: Bot },
    ],
  },
  {
    label: tx('Configuration'),
    items: [
      { label: tx('Feature Flags'), href: '/feature-flags', icon: Flag },
      { label: tx('Menu Control'), href: '/menu-control', icon: Menu },
    ],
  },
  {
    label: tx('Master Data'),
    items: [
      { label: tx('Business Types'), href: '/master-data/business-types', icon: Briefcase },
      { label: tx('Payment Methods'), href: '/master-data/payment-methods', icon: Wallet },
    ],
  },
  {
    label: tx('Communication'),
    items: [
      { label: tx('Notifications'), href: '/notifications', icon: Bell },
      { label: tx('Support'), href: '/support', icon: HeadphonesIcon },
    ],
  },
  {
    label: tx('Administration'),
    items: [
      { label: tx('Admin Users'), href: '/admin-users', icon: Users },
      { label: tx('Roles & Permissions'), href: '/permissions', icon: KeyRound },
      { label: tx('Audit Logs'), href: '/audit-logs', icon: ClipboardList },
    ],
  },
  {
    label: tx('System'),
    items: [
      { label: tx('System Settings'), href: '/system-settings', icon: Settings },
      { label: tx('Integrations'), href: '/integrations', icon: KeyRound },
      { label: tx('System Health'), href: '/system-health', icon: Activity },
    ],
  },
  {
    label: tx('Account'),
    items: [{ label: tx('My Account & 2FA'), href: '/account', icon: UserCog }],
  },
]

interface SidebarProps {
  mobileOpen: boolean
  onMobileClose: () => void
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onMobileClose }) => {
  const { currentAdmin, logout } = useAuth()
  const location = useLocation()

  const { can } = usePermission()

  // A menu shows only when the admin may open that page ("<menu>.view");
  // dashboard and my account are open to everyone.
  const canSee = (href: string): boolean => {
    if (!currentAdmin) return false
    const needed = permissionForPath(href)
    return needed === null || can(needed)
  }

  const sidebarContent = (
    <div className="flex flex-col h-full bg-white border-r border-slate-200 text-slate-800">
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 py-5 border-b border-slate-100">
        <BrandLogo size={36} />
        <div>
          <span className="text-base font-bold text-slate-900 tracking-tight">Queza</span>
          <span className="block text-[10px] text-indigo-700 font-medium leading-tight">{tx('Support Console')}</span>
        </div>
        {/* Mobile close */}
        <button
          onClick={onMobileClose}
          className="ml-auto text-slate-400 hover:text-slate-700 md:hidden"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
        {navGroups.filter((group) => group.items.some((item) => canSee(item.href))).map((group) => (
          <div key={group.label}>
            <p className="px-2 mb-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
              {group.label}
            </p>
            <ul className="space-y-0.5">
              {group.items.filter((item) => canSee(item.href)).map((item) => {
                const Icon = item.icon
                // Longest matching menu href wins, so /shops/map lights "แผนที่ร้าน" only (not "ร้านค้า" too).
                const allHrefs = navGroups.flatMap((g) => g.items.map((i) => i.href))
                const matches = (h: string) => location.pathname === h || (h !== '/dashboard' && location.pathname.startsWith(h + '/'))
                const best = allHrefs.filter(matches).sort((a, b) => b.length - a.length)[0]
                const isActive = best === item.href
                return (
                  <li key={item.href}>
                    <NavLink
                      to={item.href}
                      onClick={onMobileClose}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all group ${
                        isActive
                          ? 'bg-indigo-600 text-white shadow-sm'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                    >
                      <Icon
                        className={`h-4 w-4 flex-shrink-0 ${
                          isActive ? 'text-white' : 'text-slate-400 group-hover:text-slate-600'
                        }`}
                      />
                      <span className="flex-1 truncate">{item.label}</span>
                      {isActive && <ChevronRight className="h-3 w-3 text-white/80" />}
                    </NavLink>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* User footer */}
      {currentAdmin && (
        <div className="border-t border-slate-100 px-4 py-4">
          <div className="flex items-center gap-3">
            <NavLink to="/account" onClick={onMobileClose} title={tx('My account')} className="h-8 w-8 rounded-full bg-indigo-500 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
              {getInitials(`${currentAdmin.firstName} ${currentAdmin.lastName}`)}
            </NavLink>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">
                {currentAdmin.firstName} {currentAdmin.lastName}
              </p>
              <RoleBadge role={currentAdmin.role} label={currentAdmin.roleLabel} size="sm" />
            </div>
            <button
              onClick={logout}
              title={tx('Logout')}
              className="p-1.5 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors flex-shrink-0"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-col md:w-60 md:flex-shrink-0 h-screen sticky top-0">
        {sidebarContent}
      </div>
      {/* Mobile overlay */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-30 bg-black/50 md:hidden"
            onClick={onMobileClose}
          />
          <div className="fixed inset-y-0 left-0 z-40 w-64 md:hidden">
            {sidebarContent}
          </div>
        </>
      )}
    </>
  )
}

export default Sidebar
